import {Badge, Input, Modal} from 'antd'
import {report_manageDeleteReport_Api, report_manageResetReport_Api,} from 'apis'
import {getId, getType} from 'apis/model/base'
import _ from 'lodash'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useMemo, useState} from 'react'
import {BsChevronRight} from 'react-icons/bs'
import {DelayRender} from 'views/Discovery/DelayRender'
import ErrorReportContext, {
    ErrorReportActionTypes,
    ErrorReportProvider,
    getErrorReportActions,
} from 'views/ErrorReport/ErrorReportContext'
import {CodiReportList, ErrorReportsAdvancedFilter,} from 'views/ErrorReport/ErrorReportManager'
import renderReportItem from 'views/ErrorReport/functions/renderReportItem'
import {NoneStickyMeta} from 'views/AppLocals/EntityList'
import {AppEntities} from 'views/AppLocals/enums'
import {showDeleteConfirmDialog} from 'views/AppLocals/factory/createConfirmDialog'
import createContextMenu from 'views/AppLocals/factory/createContextMenu'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import {NotificationActionTypes, successNotify,} from 'views/AppLocals/factory/createNotification'
import {AdvancedActionGroups} from 'views/NewTemplate/components/AdvancedActionGroups'
import {NullAsDefault} from 'views/Shared'
import CRUD from 'views/Shared/enums/CRUD'

const CodiReports = (props) => {
  const t = useTranslate()

  const [keyword, setKeyword] = useState()

  const [filter, setFilter] = useState({})

  const {
    owner_id,
    owner_type,
    setSpinning = NullAsDefault,
    handleAction = NullAsDefault,
  } = useContext(ErrorReportContext)

  const onSearch = _.debounce((keyword) => {
    setKeyword(keyword)
  }, 500)

  const { handleAsyncAction: deleteReport = NullAsDefault } = useAsyncAction({
    apiInfo: report_manageDeleteReport_Api,
    onError: (e, o) => {
      notifyOnError(t)(e, o)
      setSpinning(false)
    },
    onSuccess: () => {
      setSpinning(false)
      successNotify(NotificationActionTypes.DELETE, t)
      handleAction(ErrorReportActionTypes.REFRESH.value)
    },
  })

  const { handleAsyncAction: resetReport = NullAsDefault } = useAsyncAction({
    apiInfo: report_manageResetReport_Api,
    onError: (e, o) => {
      notifyOnError(t)(e, o)
      setSpinning(false)
    },
    onSuccess: () => {
      setSpinning(false)
      successNotify(NotificationActionTypes.INFORMATION, t)
      handleAction(ErrorReportActionTypes.REFRESH.value)
    },
  })

  const handleSelect = (key, report) => {
    let confirm = {}
    const report_id = getId(report)
    switch (key) {
      case ErrorReportActionTypes.APPROVE.value:
      case ErrorReportActionTypes.REJECT.value:
        handleAction(key, report)
        break
      case ErrorReportActionTypes.DELETE.value:
        confirm = ErrorReportActionTypes.DELETE.confirm ?? {}
        showDeleteConfirmDialog({
          translate: t,
          title: confirm.title,
          message: confirm.message,
          onOk: () => {
            setSpinning(true)
            deleteReport({}, { id: report_id })
          },
        })
        break
      case ErrorReportActionTypes.RESET.value:
        confirm = ErrorReportActionTypes.RESET.confirm ?? {}
        showDeleteConfirmDialog({
          translate: t,
          title: confirm.title,
          message: confirm.message,
          onOk: () => {
            setSpinning(true)
            resetReport({}, { id: report_id })
          },
        })
        break
      default:
        break
    }
  }

  const renderContextMenu = useCallback((report) => {
    const actions = getErrorReportActions(report?.status)
    return (
      <React.Fragment>
        {createContextMenu(
          actions
            .map(({ Icon, value, className, ...rest }) => ({
              value,
              key: value,
              icon: <Icon className={className} />,
              ...rest,
            }))
            .filter(Boolean),
          (key) => {
            handleSelect(key, report)
          }
        )}
      </React.Fragment>
    )
  }, [])

  return (
    <div>
      <div className="flex flex-col gap-2">
        <Input.Search
          name="keyword"
          autoComplete="off"
          allowClear={true}
          onChange={(event) => {
            onSearch(event?.target?.value)
          }}
          onSearch={onSearch}
          placeholder={t('keyword')}
          className="input-search-with-icon"
        />
        <ErrorReportsAdvancedFilter
          className="mb-3"
          onChange={(values) => {
            setFilter(values)
          }}
          value={filter}
        />
      </div>
      <CodiReportList
        compact={true}
        id={owner_id}
        type={owner_type}
        className="space-y-3"
        values={{
          keyword,
          ...(filter ?? {}),
        }}
        renderItem={renderReportItem(AppEntities.CODI_DOCUMENT, {
          renderContextMenu,
        })}
        withPager={() => ({
          PaginationMeta: NoneStickyMeta,
        })}
        {...props}
      />
    </div>
  )
}

const CodiDocumentReportGroup = ({ item, title, description }) => {
  const t = useTranslate()

  const [action, setAction] = useState({
    type: undefined,
    value: undefined,
  })

  const total_bagde = useMemo(
    () => (
      <Badge
        className="custom-badge"
        count={item?.total_reports}>
        <span className="text-color-300 hover:text-color-000">
          {t('view detail')}
        </span>
      </Badge>
    ),
    [item.total_reports]
  )

  return (
    <React.Fragment>
      <AdvancedActionGroups.ActionGroup
        title={title}
        description={description}>
        <AdvancedActionGroups.SimpleActionButton
          label={total_bagde}
          title="list of report"
          description="list of report description"
          suffix={<BsChevronRight size={10} />}
          onClick={() => {
            setAction({
              type: CRUD.READ,
            })
          }}
        />
      </AdvancedActionGroups.ActionGroup>
      {action?.type && (
        <Modal
          width={650}
          open={action?.type}
          onCancel={() => {
            setAction({})
          }}
          destroyOnHidden={true}
          className="custom-modal"
          cancelText={t('cancel')}
          okButtonProps={{ hidden: true }}
          title={
            <div className="text-center font-bold text-color-000 uppercase tracking-wide">
              {t('reports')}
            </div>
          }
          cancelButtonProps={{
            className: 'button-rounded-lg no-shadow no-text-shadow no-border',
          }}>
          <DelayRender lazy={true}>
            <ErrorReportProvider
              owner_id={getId(item)}
              owner_type={getType(item)}>
              <CodiReports />
            </ErrorReportProvider>
          </DelayRender>
        </Modal>
      )}
    </React.Fragment>
  )
}

export default CodiDocumentReportGroup
